import type { NexxusDatabase } from '@infra/persistence/NexxusDatabase';
import type { Tenant } from '@shared/types/Tenant';
import type { Salarie } from '@shared/types/Salarie';
import type { Bulletin } from '@shared/types/Bulletin';
import type { Ecriture } from '@shared/types/Ecriture';
import { uuid } from '@shared/utils/uuid';
import { logger } from '@infra/telemetry/Logger';

interface StateV22 {
  societe?: Record<string, unknown>;
  salaries?: Record<string, unknown>[];
  bulletins?: Record<string, unknown>[];
  ecritures?: Record<string, unknown>[];
  users?: Record<string, unknown>[];
  [key: string]: unknown;
}

export interface MigrationReport {
  migrated: boolean;
  reason?: string;
  tenant_id?: string;
  counts: {
    salaries: number;
    bulletins: number;
    ecritures: number;
    users: number;
  };
  errors: string[];
  duration_ms: number;
}

const STATE_KEY_V22 = 'nexxus_state';
const MIGRATED_FLAG = 'nexxus_migrated_to_v23';
const BACKUP_KEY = 'nexxus_backup_v22_for_rollback';

/**
 * Migration v2.2 → v2.3.
 * Détecte localStorage v2.2, sauvegarde, parse, distribue dans IndexedDB.
 */
export async function migrateV22ToV23(db: NexxusDatabase): Promise<boolean> {
  const report = await runMigrationWithReport(db);
  return report.migrated;
}

export async function runMigrationWithReport(db: NexxusDatabase): Promise<MigrationReport> {
  const start = performance.now();
  const report: MigrationReport = {
    migrated: false,
    counts: { salaries: 0, bulletins: 0, ecritures: 0, users: 0 },
    errors: [],
    duration_ms: 0,
  };

  if (typeof localStorage === 'undefined') {
    report.reason = 'localStorage indisponible (probablement Node)';
    return report;
  }
  if (localStorage.getItem(MIGRATED_FLAG)) {
    report.reason = 'Déjà migré';
    return report;
  }
  const raw = localStorage.getItem(STATE_KEY_V22);
  if (!raw) {
    report.reason = 'Aucune donnée v2.2 trouvée';
    return report;
  }

  let state: StateV22;
  try {
    state = JSON.parse(raw) as StateV22;
  } catch (err) {
    report.reason = 'localStorage v2.2 invalide';
    report.errors.push(String(err));
    return report;
  }

  // Sauvegarde rollback
  localStorage.setItem(BACKUP_KEY, raw);
  logger.info({ event: 'migration_backup_saved', size: raw.length });

  try {
    // 1) Tenant à partir de la société
    const societe = state.societe ?? {};
    const now = new Date().toISOString();
    const nom = String(societe['raison_sociale'] ?? societe['nom'] ?? 'Société importée');
    const tenant: Tenant = {
      id: String(societe['id'] ?? uuid('tnt')),
      code: String(societe['code'] ?? 'IMPORT'),
      raison_sociale: nom,
      nom,
      nif: String(societe['nif'] ?? ''),
      tier: (societe['tier'] as Tenant['tier']) ?? 'standard',
      mode_deploiement:
        ((societe['mode_deploiement'] ??
          societe['deployment_mode']) as Tenant['mode_deploiement']) ?? 'standalone',
      deployment_mode:
        ((societe['mode_deploiement'] ??
          societe['deployment_mode']) as Tenant['mode_deploiement']) ?? 'standalone',
      forme_juridique: (societe['forme_juridique'] as Tenant['forme_juridique']) ?? 'SARL',
      regime_fiscal: (societe['regime_fiscal'] as Tenant['regime_fiscal']) ?? 'normal',
      capital_social: Number(societe['capital_social']) || 0,
      date_creation: String(societe['date_creation'] ?? now.slice(0, 10)),
      tier_history: [],
      periode_courante: now.slice(0, 7),
      exercice_courant: now.slice(0, 4),
      parametres: (societe['parametres'] as Tenant['parametres']) ?? { devise_principale: 'XAF' },
      settings: (societe['settings'] as Tenant['settings']) ?? {},
      cree_le: String(societe['cree_le'] ?? now),
    };
    if (societe['nif']) tenant.nif = String(societe['nif']);
    if (societe['rccm']) tenant.rccm = String(societe['rccm']);
    if (societe['adresse']) tenant.adresse = String(societe['adresse']);

    await db.transaction('rw', db.tenants, db.salaries, db.bulletins, db.ecritures, async () => {
      await db.tenants.put(tenant);
      report.tenant_id = tenant.id;

      // 2) Salariés
      for (const s of state.salaries ?? []) {
        try {
          const sal: Salarie = {
            id: String(s['id'] ?? uuid('sal')),
            tenant_id: tenant.id,
            matricule: String(s['matricule'] ?? ''),
            nom: String(s['nom'] ?? ''),
            prenom: String(s['prenom'] ?? ''),
            date_embauche: String(s['date_embauche'] ?? new Date().toISOString().slice(0, 10)),
            categorie: (s['categorie'] as Salarie['categorie']) ?? 'Employe',
            statut: (s['statut'] as Salarie['statut']) ?? 'actif',
            salaire_base: Number(s['salaire_base']) || 150_000,
            situation_familiale:
              (s['situation_familiale'] as Salarie['situation_familiale']) ?? 'celibataire',
            situation: (s['situation_familiale'] as Salarie['situation']) ?? 'celibataire',
            nb_enfants: Number(s['nb_enfants']) || 0,
            // A-04 : le moteur lit `enfants`, pas `nb_enfants`
            enfants: Number(s['enfants'] ?? s['nb_enfants']) || 0,
            conjoint_actif: Boolean(s['conjoint_actif']),
            cree_le: String(s['cree_le'] ?? new Date().toISOString()),
          };
          if (s['date_naissance']) sal.date_naissance = String(s['date_naissance']);
          if (s['email']) sal.email = String(s['email']);
          if (s['nif']) sal.nif = String(s['nif']);
          if (s['numero_cnss']) sal.numero_cnss = String(s['numero_cnss']);
          await db.salaries.put(sal);
          report.counts.salaries++;
        } catch (err) {
          report.errors.push(`Salarié ${s['matricule'] ?? 'inconnu'} : ${String(err)}`);
        }
      }

      // 3) Bulletins
      for (const b of state.bulletins ?? []) {
        try {
          const bul: Bulletin = {
            id: String(b['id'] ?? uuid('bul')),
            tenant_id: tenant.id,
            salarie_id: String(b['salarie_id'] ?? ''),
            periode: String(b['periode'] ?? ''),
            salaire_base: Number(b['salaire_base']) || 0,
            brut_base: Number(b['brut_base'] ?? b['salaire_base']) || 0,
            sursalaire: Number(b['sursalaire']) || 0,
            prime: Number(b['prime']) || 0,
            prime_extra: Number(b['prime_extra']) || 0,
            heures_supplementaires: Number(b['heures_supplementaires']) || 0,
            avance: Number(b['avance']) || 0,
            brut_imposable: Number(b['brut_imposable']) || 0,
            cnss_vid_sal: Number(b['cnss_vid_sal']) || 0,
            cnamgs_sal: Number(b['cnamgs_sal']) || 0,
            irpp_mensuel: Number(b['irpp_mensuel']) || 0,
            net_a_payer: Number(b['net_a_payer']) || 0,
            cout_employeur: Number(b['cout_employeur']) || 0,
            parts_fiscales: Number(b['parts_fiscales']) || 1,
            statut: (b['statut'] as Bulletin['statut']) ?? 'genere',
            date_emission: String(b['date_emission'] ?? b['genere_le'] ?? new Date().toISOString()),
            base_cnss: Number(b['base_cnss']) || 0,
            base_cnamgs: Number(b['base_cnamgs']) || 0,
            cnss_pf_emp: 0,
            cnss_rp_emp: 0,
            cnss_vid_emp: 0,
            cnamgs_emp: 0,
            cnamgs_fam_emp: 0,
            cfp_emp: 0,
            fnh_emp: 0,
            fgtls_emp: 0,
            tcs: 0,
            total_charges_emp: 0,
            parts: Number(b['parts'] ?? b['parts_fiscales']) || 1,
            abattement: 0,
            net_imposable: 0,
            css: Number(b['css']) || 0,
            cree_le: String(b['cree_le'] ?? b['genere_le'] ?? new Date().toISOString()),
          };
          await db.bulletins.put(bul);
          report.counts.bulletins++;
        } catch (err) {
          report.errors.push(`Bulletin ${b['id'] ?? 'inconnu'} : ${String(err)}`);
        }
      }

      // 4) Écritures
      for (const e of state.ecritures ?? []) {
        try {
          const ecr: Ecriture = {
            id: String(e['id'] ?? uuid('ecr')),
            tenant_id: tenant.id,
            date: String(e['date'] ?? ''),
            periode: String(e['periode'] ?? ''),
            journal: (e['journal'] as Ecriture['journal']) ?? 'OD',
            compte: String(e['compte'] ?? ''),
            libelle_compte: String(e['libelle_compte'] ?? ''),
            libelle: String(e['libelle'] ?? ''),
            debit: Number(e['debit']) || 0,
            credit: Number(e['credit']) || 0,
            source: (e['source'] as Ecriture['source']) ?? 'manuel',
            genere_le: String(e['genere_le'] ?? new Date().toISOString()),
          };
          if (e['numero_piece']) ecr.numero_piece = String(e['numero_piece']);
          if (e['axe_cdc']) ecr.axe_cdc = String(e['axe_cdc']);
          await db.ecritures.put(ecr);
          report.counts.ecritures++;
        } catch (err) {
          report.errors.push(`Écriture ${e['id'] ?? 'inconnu'} : ${String(err)}`);
        }
      }
    });

    localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());
    report.migrated = true;
    logger.info({
      event: 'migration_done',
      counts: report.counts,
      duration: performance.now() - start,
    });
  } catch (err) {
    report.errors.push(`Migration globale : ${String(err)}`);
    logger.error({ event: 'migration_failed', error: String(err) });
  }

  report.duration_ms = Math.round(performance.now() - start);
  return report;
}

/** Rollback : restaure le localStorage v2.2 et supprime les flags. */
export function rollbackMigration(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const backup = localStorage.getItem(BACKUP_KEY);
  if (!backup) return false;
  localStorage.setItem(STATE_KEY_V22, backup);
  localStorage.removeItem(MIGRATED_FLAG);
  return true;
}
